import {useSelector} from "react-redux";
import {
  selectCurrentPlayer,
  selectGameResults,
  selectIsAgrarianismConversionOn,
  selectIsGameStarted,
  selectPendingMeleeDamage,
  selectPendingShootingActions,
  selectPhase,
  selectPlayers,
  selectSelectedUnit,
  selectSharedGameState,
  selectWarPhaseStage
} from "../../redux/reducers/sharedStateReducer/selectors";
import {PHASES, WAR_PHASE_STAGES} from "../../constants/phases";
import {selectSelectedUnitId, selectShowMeleeTargets} from "../../redux/reducers/privateStateReducer/selectors";
import {TEXT_CHOOSE_MELEE_PRIORITY_TARGET} from "../../constants/text/combat";
import {useIsCurrentUserTurn} from "../../utils/gameState/user/useIsCurrentUserCurrentPlayer";
import {getPlayerHexColor} from "../../utils/players/getPlayerHexColor";
import {isPlayerFaction} from "../../utils/factions/isPlayerFaction";
import {isUnitFaction} from "../../utils/factions/isUnitFaction";
import {FACTIONS} from "../../constants/factions";
import {UNIT_TYPES} from "../../configs/units";
import {checkIfMeleeDamageDetailsShown} from "../../utils/gameState/melee/checkIfMeleeDamageDetailsShown";
import {checkIfConstructionPlanningAvailable} from "../../utils/gameState/construction/planning/getConstructionPlanContext";
import {
  selectIsParallelSupplyPhase,
  selectSupplyActingPlayer,
  selectTurnDisplayedPlayer
} from "../../utils/gameState/supply/parallelSupplySelectors";
import {checkIfPlayerCompletedPhase, getNotCompletedPlayers} from "../../utils/gameState/supply/parallelSupply";
import {getActingPlayerFromState} from "../../utils/players/getActingPlayer";

// Чужой ход строительства, а свой еще впереди: можно планировать свои строения
// (см. PlanningBuildingToolbar) — они будут построены в начале своего хода.
// Заголовок: «Пока {ходящий} строит, планируй свои строения»
export const TEXT_CONSTRUCTION_PLANNING_TITLE_PREFIX = 'Пока ';
export const TEXT_CONSTRUCTION_PLANNING_TITLE_SUFFIX = ' строит, планируй свои строения';

// Одновременная фаза снабжения сетевой игры (см. utils/gameState/supply/parallelSupply)
export const TEXT_SUPPLY_DISTRIBUTE = 'Распределите снабжение';
export const TEXT_SUPPLY_AGRARIANISM_CONVERSION = 'Выберите снабжение для обмена на монеты';
export const TEXT_SUPPLY_DISTRIBUTING_SUFFIX = ' распределяет снабжение отрядам';
// Свою часть фазы игрок закончил, а снабжение раздают еще несколько соперников
export const TEXT_PARALLEL_SUPPLY_WAIT_OTHER_PLAYERS = 'Снабжение распределено, ожидаем других игроков';

// Действующий игрок клиента (сам пользователь или бот, которым управляет
// админ) уже закончил свою часть одновременной фазы снабжения. У зрителя и в
// записи матча — false: его «в игре» нет
const selectIsActingPlayerCompletedParallelSupply = (state) => {
  const gameState = selectSharedGameState(state);
  const actingPlayer = getActingPlayerFromState(state);
  const isGamePlayer = Boolean(actingPlayer)
    && (selectPlayers(state) || []).some((player) => player.id === actingPlayer.id);
  return isGamePlayer && checkIfPlayerCompletedPhase(gameState, actingPlayer.id);
};

const selectNotCompletedPlayersCount = (state) => getNotCompletedPlayers(selectSharedGameState(state)).length;

function formatPlayerTitle(player, suffix) {
  return (
    <>
      <span style={{color: getPlayerHexColor(player.color)}}>{player.name}</span>
      {suffix}
    </>
  );
}

// Самый длинный заголовок футера: на телефоне с длинным ником он занимал три
// строки и всю высоту шапки — вне десктопа шрифт у него мельче (см. Footer.css)
function formatConstructionPlanningTitle(player) {
  return (
    <span className="footer-heading-description_long">
      {TEXT_CONSTRUCTION_PLANNING_TITLE_PREFIX}
      <span className="footer-heading-description_long-name" title={player.name} style={{color: getPlayerHexColor(player.color)}}>{player.name}</span>
      {TEXT_CONSTRUCTION_PLANNING_TITLE_SUFFIX}
    </span>
  );
}

// Залп выбранного отряда еще летит: результата под заголовком пока нет (панель
// откроется с попаданием), а выбирать цель уже поздно
export const TEXT_SHOT_IN_FLIGHT_UNIT = 'Отряд стреляет';
// Выбран участник боя, а удары на поле еще идут: расчета урона под
// заголовком пока нет (панель откроется после ударов)
export const TEXT_MELEE_IN_PROGRESS = 'Отряды сражаются';
export const TEXT_SHOT_IN_FLIGHT_CATAPULT = 'Катапульта стреляет';

// Катапульта ли выбранный стрелок — по самому отряду, а не по фракции игрока:
// стрелков-эльфов мог переманить игрок другой фракции, а катапульты есть и у
// эльфов. Выбран не стрелок — как раньше, по фракции игрока
const checkIfCatapultSelected = (unit, player) => {
  if (unit?.type === UNIT_TYPES.D4.id) {
    return true;
  }
  if (unit && isUnitFaction(unit, FACTIONS.ELVES)) {
    return false;
  }
  return !isPlayerFaction(player, FACTIONS.ELVES);
};
const getChooseTargetTitle = (unit, player) => (
  checkIfCatapultSelected(unit, player) ? 'Выберите цель для катапульты' : 'Выберите цель для отряда'
);
const getShotInFlightTitle = (unit, player) => (
  checkIfCatapultSelected(unit, player) ? TEXT_SHOT_IN_FLIGHT_CATAPULT : TEXT_SHOT_IN_FLIGHT_UNIT
);

/**
 * @param {Object} [options]
 * @param {boolean} [options.isShootingResultRevealed] — показан ли под
 *   заголовком результат стрельбы выбранного отряда (см.
 *   useShootingResultRevealed): пока залп летит, заголовок его не обещает
 * @param {boolean} [options.isMeleeResultRevealed] — показан ли под
 *   заголовком расчет урона ближнего боя выбранного отряда (см.
 *   useMeleeResultRevealed): пока идут удары, заголовок его не обещает
 */
export function useFooterTitle({isShootingResultRevealed = true, isMeleeResultRevealed = true} = {}) {
  const gameResults = useSelector(selectGameResults);
  const isGameStarted = useSelector(selectIsGameStarted);
  const phase = useSelector(selectPhase);
  const warPhaseStage = useSelector(selectWarPhaseStage);
  const showMeleeTargets = useSelector(selectShowMeleeTargets);
  const currentPlayer = useSelector(selectCurrentPlayer);
  const isCurrentUserTurn = useIsCurrentUserTurn();
  const selectedUnitId = useSelector(selectSelectedUnitId);
  const selectedUnit = useSelector(selectSelectedUnit);
  const pendingShootingActions = useSelector(selectPendingShootingActions);
  const pendingMeleeDamage = useSelector(selectPendingMeleeDamage);
  // Выбор обмена по аграризму — того игрока, за которого клиент раздает
  // снабжение (в поочередной фазе это текущий игрок, как и раньше)
  const isAgrarianismConversionOn = useSelector((state) => selectIsAgrarianismConversionOn(
    state, selectSupplyActingPlayer(state)?.id ?? null
  ));
  const isConstructionPlanning = useSelector(checkIfConstructionPlanningAvailable);
  const isParallelSupplyPhase = useSelector(selectIsParallelSupplyPhase);
  const isActingPlayerCompletedParallelSupply = useSelector(selectIsActingPlayerCompletedParallelSupply);
  const notCompletedPlayersCount = useSelector(selectNotCompletedPlayersCount);
  const turnDisplayedPlayer = useSelector(selectTurnDisplayedPlayer);

  const isMeleeDamageDetailsShown = checkIfMeleeDamageDetailsShown({
    phase, warPhaseStage, selectedUnit, showMeleeTargets, pendingMeleeDamage,
  });

  if (gameResults) {
    return 'Результаты:'
  }

  if (!isGameStarted) {
    return 'Жетоны:'
  }

  switch (phase) {
    case PHASES.SETUP:
      return 'Размести стартовый квартал на любой ячейке с флагом твоего цвета'
    case PHASES.SUPPLY:
      if (isCurrentUserTurn) {
        if (isAgrarianismConversionOn) {
          return TEXT_SUPPLY_AGRARIANISM_CONVERSION
        }
        return TEXT_SUPPLY_DISTRIBUTE
      } else if (isParallelSupplyPhase) {
        // Одновременная фаза: свою часть пользователь закончил (или он зритель).
        // Закончившему, пока снабжение раздают несколько соперников, — общее
        // ожидание; иначе — кто раздает (первый в порядке хода, как в шапке,
        // см. selectTurnDisplayedPlayer)
        if (isActingPlayerCompletedParallelSupply && notCompletedPlayersCount !== 1) {
          return TEXT_PARALLEL_SUPPLY_WAIT_OTHER_PLAYERS
        }
        return formatPlayerTitle(turnDisplayedPlayer, TEXT_SUPPLY_DISTRIBUTING_SUFFIX)
      } else {
        return formatPlayerTitle(currentPlayer, TEXT_SUPPLY_DISTRIBUTING_SUFFIX)
      }
    case PHASES.CONSTRUCTION:
      if (isCurrentUserTurn) {
        return 'Размести строения'
      } else if (isConstructionPlanning) {
        return formatConstructionPlanningTitle(currentPlayer)
      } else {
        return formatPlayerTitle(currentPlayer, ' строит')
      }
    case PHASES.WAR:
      switch (warPhaseStage) {
        case WAR_PHASE_STAGES.RECRUITMENT:
          if (isCurrentUserTurn) {
            return 'Отряды для найма:'
          } else {
            return formatPlayerTitle(currentPlayer, ' нанимает отряды')
          }
        case WAR_PHASE_STAGES.MOVEMENT:
          if (isCurrentUserTurn) {
            return 'Переместите отряды'
          } else {
            return formatPlayerTitle(currentPlayer, ' перемещает отряды')
          }
        case WAR_PHASE_STAGES.ABILITIES:
          if (isCurrentUserTurn) {
            return 'Используйте способности отрядов'
          } else {
            return formatPlayerTitle(currentPlayer, ' использует способности отрядов')
          }
        case WAR_PHASE_STAGES.SHOOTING:
          if(isPlayerFaction(currentPlayer, FACTIONS.ELVES)) {
            if (isCurrentUserTurn) {
              if (!selectedUnitId) {
                return 'Выберите отряды для стрельбы'
              } else {
                if (pendingShootingActions.some(action => action.shooterUnitId === selectedUnitId)) {
                  return isShootingResultRevealed
                    ? 'Результат стрельбы выбранного отряда'
                    : getShotInFlightTitle(selectedUnit, currentPlayer)
                } else {
                  return getChooseTargetTitle(selectedUnit, currentPlayer)
                }
              }
            } else {
              return formatPlayerTitle(currentPlayer, ' стреляет отрядами')
            }
          } else {
            if (isCurrentUserTurn) {
              if (!selectedUnitId) {
                return 'Выберите катапульты для стрельбы'
              } else {
                if (pendingShootingActions.some(action => action.shooterUnitId === selectedUnitId)) {
                  return isShootingResultRevealed
                    ? 'Результат стрельбы выбранного отряда'
                    : getShotInFlightTitle(selectedUnit, currentPlayer)
                } else {
                  return getChooseTargetTitle(selectedUnit, currentPlayer)
                }
              }
            } else {
              return formatPlayerTitle(currentPlayer, ' стреляет катапультами')
            }
          }
        case WAR_PHASE_STAGES.COMBAT:
          if(showMeleeTargets) {
            return TEXT_CHOOSE_MELEE_PRIORITY_TARGET;
          }
          // Пока отряд не выбран — приглашение выбрать; как только под
          // заголовком появился расчет урона (SelectedUnitMelee), заголовок
          // называет именно его. Тот же прием, что на этапе стрельбы выше
          if (isMeleeDamageDetailsShown) {
            return isMeleeResultRevealed ? 'Расчет урона, который нанёс выбранный отряд' : TEXT_MELEE_IN_PROGRESS
          }
          return 'Выберите отряд для просмотра деталей'
        case WAR_PHASE_STAGES.MANEUVER:
          if (isCurrentUserTurn) {
            return 'Выполните маневр отрядами с дополнительным снабжением'
          } else {
            return formatPlayerTitle(currentPlayer, ' выполните маневр отрядами с дополнительным снабжением')
          }
        default:
          return 'Война ???'
      }
    default:
      return '???'
  }
}